import React, { useEffect, useState, useMemo, Fragment } from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  Select,
  MenuItem,
  Stack,
  Box,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Link,
  FormControl,
  InputLabel,
  IconButton,
  Menu
} from "@mui/material"
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { SubtitleInline } from "components/shared/Typography";
import { getQuarter, getYear } from "date-fns";
import { EventNote, Check } from "@mui/icons-material"
import {clientActions} from "redux/clientSlice";
import {vendorActions} from "redux/vendorSlice";
import {useDispatch, useSelector} from "react-redux";
import { awsDateToUS } from "utils/dateUtils"
import { getLogo } from "services/appResources";
import StatusIndicator from "components/shared/StatusIndicator";
import {organizationState} from "redux/organizationSlice";
import {loggedInUserState} from "redux/userSlice";
import {getNavigationPath, NAVIGATED_FROM, useNavPath} from "constants/routes";
import TableLoadingIndicator from "components/shared/TableLoadingIndicator";
import SortableTHeadCell from "components/shared/SortableTHeadCell"
import { Certificatestatus, Vendorstatus, Insurancetype } from "models"
import EmailConfirmationModal from "components/VendorDetails/EmailConfirmationModal";
import certificate_actions from "constants/certificate_actions";
import Snackbar from "components/shared/Snackbar";

const dateMax = "3000-12-31"
const getQueryFilter = (year) => ([
  [`${year}-01-01`, `${year}-12-31`],
  [`${year}-01-01`, `${year}-03-31`],
  [`${year}-04-01`, `${year}-06-30`],
  [`${year}-07-01`, `${year}-09-30`],
  [`${year}-10-01`, `${year}-12-31`]
])
const sortFunctions = {
  vendorName: (cert1, cert2, asc, keyedVendors) => {
    const vendorName1 = keyedVendors?.[cert1.clientVendorID]?.vendor?.name || ""
    const vendorName2 = keyedVendors?.[cert2.clientVendorID]?.vendor?.name || ""
    return asc ? vendorName1.localeCompare(vendorName2) : vendorName2.localeCompare(vendorName1)
  },
  certStatus: (cert1, cert2, asc) => {
    const status1 = cert1.status || ""
    const status2 = cert2.status || ""
    return asc ? status1.localeCompare(status2) : status2.localeCompare(status1)
  },
  vendorStatus: (cert1, cert2, asc, keyedVendors) => {
    const vendorStatus1 = keyedVendors?.[cert1.clientVendorID]?.status
    const vendorStatus2 = keyedVendors?.[cert2.clientVendorID]?.status
    return asc ? vendorStatus1.localeCompare(vendorStatus2) : vendorStatus2.localeCompare(vendorStatus1)
  },
  policyExp: (cert1, cert2, asc) => {
    const getEarliestString = (cert) => {
      let coverages = []
      try {
        coverages = JSON.parse(cert.coverages)
      } catch(e) {
        console.error(e)
      }
      const allDates = coverages?.flatMap(cov => cov.policyExp) || []
      return allDates.reduce((min, curr) => (curr || dateMax) < (min || dateMax) ? curr : min, dateMax)
    }

    const aMin = getEarliestString(cert1) || dateMax
    const bMin = getEarliestString(cert2) || dateMax
    return asc ? aMin.localeCompare(bMin) : bMin.localeCompare(aMin)
  }
}

const ColumnMultiFilter = ({
  name,
  label,
  values,
  selectedValues,
  setValues,
  renderMenuContent
}) => {

  return (
    <FormControl fullWidth size="small">
      {!selectedValues?.length && <InputLabel id={`${name}Filter`}>Filter {label}</InputLabel>}
      <Select
        size="small"
        labelId={`${name}Filter`}
        label={!selectedValues?.length ? `Filter ${label}` : ""}
        multiple
        onChange={event => {
          if(event.target.value.includes("")) {
            setValues([])
          } else {
            setValues(event.target.value)
          }
        }}
        value={selectedValues}
        renderValue={(selected) => {
          if(selected) {
            return `Filter ${label}`
          }
          return `All ${label}`
        }}
      >
        <MenuItem display="flex" value={""}>
          <Box flexGrow={1} mr={1}>All {label}</Box>
          {!selectedValues?.length && <Check />}
        </MenuItem>
        {
          values.map(value => (
            <MenuItem display="flex" value={value}>
              <Box flexGrow={1} mr={1}>
                {renderMenuContent ? renderMenuContent(value) : value}
              </Box>
              {selectedValues?.includes(value) && <Check />}
            </MenuItem>
          ))
        }
      </Select>
    </FormControl>
  )
}

export default function QuarterlyComplianceTable() {
  const dispatch = useDispatch()
  const [selectedVendors, setSelectedVendors] = useState([])
  const [selectedVendorStatuses, setSelectedVendorStatuses] = useState([])
  const [selectedCertStatuses, setSelectedCertStatuses] = useState([])
  const [selectedPolicyTypes, setSelectedPolicyTypes] = useState([])
  const [sortBy, setSortBy] = useState('policyExp')
  const [sortAsc, setSortAsc] = useState(true)
  const [selectedQuarter, setSelectedQuarter] = useState("0");
  const [selectedYear, setSelectedYear] = useState(getYear(new Date()).toString())
  const [isVendorLoading, setIsVendorLoading] = useState(false)
  const [isCertLoading, setIsCertLoading] = useState(false)
  const isLoading = isVendorLoading || isCertLoading
  const { userOrgType, selectedClient: client } = useSelector(organizationState)
  const { loggedInUser } = useSelector(loggedInUserState)
  const { organizationID } = (client || {})
  const vendorLabel = client?.vendorLabel || "Subcontractor"
  const [clientVendors, setClientVendors] = useState({})
  const [clientProjects, setClientProjects] = useState([])
  const NAV_PATH = useNavPath(userOrgType)
  const [certificates, setCertificates] = useState([])
  const navigate = useNavigate()
  const location = useLocation()
  const theadProps = { sortBy, setSortBy, sortAsc, setSortAsc }
  
  // Email reminder state
  const [emailModalOpen, setEmailModalOpen] = useState(false)
  const [selectedVendorForEmail, setSelectedVendorForEmail] = useState(null)
  const [anchorEl, setAnchorEl] = useState(null)
  const [message, setMessage] = useState(null)
  const [clientLogo, setClientLogo] = useState("")

  const goToCertificate = (certificateId) => {
    const certificatePath = getNavigationPath(NAV_PATH.CLIENT_CERTIFICATE_DETAILS, {
      clientId: organizationID,
      vendorId: NAVIGATED_FROM.CERTIFICATES,
      certificateId: certificateId
    })
    navigate(certificatePath, {
      state: {
        referrer: location.pathname
      }
    })
  }

  const navToVendor = (vendorId) => {
    const destination = getNavigationPath(NAV_PATH.CLIENT_VENDOR_DETAILS, {
      clientId: organizationID,
      vendorId,
    })

    navigate(destination)
  }

  const handleMenuClick = (event, vendorId) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedVendorForEmail(vendorId);
  }

  const handleMenuClose = () => {
    setAnchorEl(null);
  }

  const handleSendReminder = () => {
    const vendorId = selectedVendorForEmail;
    handleMenuClose();
    // Use setTimeout to ensure menu closes before modal opens
    setTimeout(() => {
      setSelectedVendorForEmail(vendorId);
      setEmailModalOpen(true);
    }, 0);
  }

  const onEmailSentResult = (success) => {
    if (success) {
      setMessage({
        text: "Certificate reminder sent successfully! The vendor will receive an email with instructions to upload their certificate.",
        severity: "success",
        duration: 6000,
      });
      // Refresh certificates to ensure Email Activity gets updated data
      setTimeout(() => {
        getCertificates();
      }, 2000);
    } else {
      setMessage({
        text: "Unable to send certificate reminder. Please try again.",
        severity: "error",
      });
    }
  }

  const getUploadLink = async (certificateId, projectID, mode, recipients, requestedRequirements, initialDescriptionOfOperation) => {
    if (certificateId === "create") {
      try {
        const vendor = clientVendors[selectedVendorForEmail];
        // Filter out empty email addresses
        const validRecipients = recipients.filter(email => email && email.trim() !== '');
        console.log('Creating certificate reminder for vendor:', vendor.vendor.name, 'with recipients:', validRecipients);
        
        const result = await dispatch(
          vendorActions.createRequestCertificate({
            clientVendorID: vendor.id,
            clientID: client.clientID,
            sentBy: loggedInUser.email,
            sentTo: validRecipients,
            ...(projectID ? { projectID } : {}),
          })
        );
        
        if (!result.payload || !result.payload.certificateId) {
          throw new Error('Failed to create certificate request');
        }
        
        console.log('Certificate request created successfully:', result.payload.certificateId);
        return window.location.origin + `/vendor/upload/${client.id}/${vendor.vendor.id}/${result.payload.certificateId}`;
      } catch (error) {
        console.error('Error creating certificate request:', error);
        throw error;
      }
    }
  }

  async function getClientVendors() {
    setIsVendorLoading(true)
    const vendors = {}
    const result = await dispatch(clientActions.listClientVendorsForClient({ clientID: client.clientID }))
    if(result?.payload?.clientVendors?.length) {
      result.payload.clientVendors.forEach(clientVendor => (vendors[clientVendor.id] = clientVendor))
    }
    setClientVendors(vendors)
    setIsVendorLoading(false)
  }

  async function getCertificates() {
    setIsCertLoading(true)
    const filter = {
      firstExp: {
        between: getQueryFilter(selectedYear)[selectedQuarter]
      },
      status: {
        ne: "NOT_APPROVED"
      }
    }
    const result = await dispatch(clientActions.listClientCertificates({
      filter,
      clientID: client.clientID
    }))
    let certs = []
    if(result?.payload?.certificates?.length) {
      result?.payload?.certificates.forEach(cert => {
        certs.push({
          ...cert,
          // set state is trimming the nested array to 1 element for weird reason
          // stringify to preserve it
          coverages: JSON.stringify(
            cert.coverages.items?.length
              ? cert.coverages.items
                .filter(coverage => coverage.policyExp?.length || coverage.policyNumber)
                .map(coverage => ({
                  ...coverage,
                  policyExp: coverage.policyExp.sort((exp1, exp2) => (exp1 || dateMax).localeCompare(exp2 || dateMax))
                }))
                .sort((cov1, cove2) => (cov1.policyExp?.[0] || dateMax).localeCompare(cove2.policyExp?.[0] || dateMax))
              : []
          )
        })
      })
    }
    setCertificates(certs)
    setIsCertLoading(false)
  }

  useEffect(() => {
    if(client) {
      getClientVendors()
      // Fetch projects if client uses project mode
      if (client.mode === 'project') {
        dispatch(clientActions.fetchProjects(client.clientID)).then(result => {
          if (result.payload) {
            setClientProjects(result.payload);
          }
        });
      }
      if (client.logo) {
        getLogo(client.logo, (response) => setClientLogo(response));
      }
    }
  }, [client])

  useEffect(() => {
    if(client) {
      getCertificates()
    }
  }, [client, selectedYear, selectedQuarter])

  const filteredCertificates = useMemo(() => {
    let certs
    if(
      certificates?.length &&
      (selectedVendors.length || selectedVendorStatuses.length || selectedCertStatuses.length || selectedPolicyTypes.length)) {
      certs = certificates.filter(cert => {
        return (!selectedVendors.length || selectedVendors.includes(cert.clientVendorID)) &&
          (!selectedVendorStatuses.length || selectedVendorStatuses.includes(clientVendors[cert.clientVendorID].status)) &&
          (!selectedCertStatuses.length || selectedCertStatuses.includes(cert.status)) &&
          (!selectedPolicyTypes.length || selectedPolicyTypes.find(policyType => cert.coverages?.includes(`"insuranceType":"${policyType}"`)))
      })
    } else {
      certs = certificates
    }
    return certs?.sort((a, b) => sortFunctions[sortBy](a, b, sortAsc, clientVendors))
  }, [
    certificates,
    sortBy,
    sortAsc,
    selectedVendors,
    selectedVendorStatuses,
    selectedCertStatuses,
    selectedPolicyTypes
  ])

  return (
    <Card variant="outlined">
      <CardHeader
        title={'Quarterly Certificate Expirations'}
        subheader={'View certificate expirations by quarter and year'}
      >
      </CardHeader>
      <CardContent>
        <Stack gap={4}>
          <Stack direction="row" gap={2}>
            <Box width={"15%"}>
              <SubtitleInline>Quarter</SubtitleInline>
              <Select
                value={selectedQuarter}
                onChange={event => setSelectedQuarter(event.target.value)}
                fullWidth={true}
                size="small"
              >
                <MenuItem value={"0"}>All</MenuItem>
                <MenuItem value={"1"}>Q1 (Jan-Mar)</MenuItem>
                <MenuItem value={"2"}>Q2 (Apr-Jun)</MenuItem>
                <MenuItem value={"3"}>Q3 (Jul-Sep)</MenuItem>
                <MenuItem value={"4"}>Q4 (Oct-Dec)</MenuItem>
              </Select>
            </Box>

            <Box width={"15%"}>
              <SubtitleInline>Year</SubtitleInline>
              <Select
                value={selectedYear}
                onChange={event => setSelectedYear(event.target.value)}
                fullWidth={true}
                size="small"
              >
                {[...Array(5)].map((_, i) => {
                  const year = getYear(new Date()) + i;
                  return (
                    <MenuItem key={year} value={year.toString()}>
                      {year}
                    </MenuItem>
                  );
                })}
              </Select>
            </Box>
            <Box flexGrow={1}></Box>
            <FormControl sx={{width: "20%"}}>
              <InputLabel id="vendorFilter">Filter By {vendorLabel}</InputLabel>
              <Select
                labelId="vendorFilter"
                label={`Filter By ${vendorLabel}`}
                multiple
                onChange={event => {
                  if(event.target.value.includes("")) {
                    setSelectedVendors([])
                  } else {
                    setSelectedVendors(event.target.value)
                  }
                }}
                value={selectedVendors}
                renderValue={(selected) => {
                  return Object.values(clientVendors)
                    .filter(clientVendor => selectedVendors.includes(clientVendor.id))
                    .map(clientVendor => clientVendor.vendor.name)
                    .join(", ")
                }}
              >
                <MenuItem display="flex" value={""}>
                  <Box flexGrow={1} mr={1}>All {vendorLabel}</Box>
                  {!selectedVendors.length && <Check />}
                </MenuItem>
                {Object.values(clientVendors)?.map(clientVendor => (
                  <MenuItem value={clientVendor.id} display="flex" key={clientVendor.id}>
                    <Box flexGrow={1} mr={1}>{clientVendor.vendor.name}</Box>
                    {selectedVendors.includes(clientVendor.id) && <Check />}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <TableContainer sx={{border: "1px solid #e0e0e0", borderRadius: '4px'}}>
            <Table>
              <TableHead>
                <TableRow>
                  <SortableTHeadCell
                    label={vendorLabel}
                    sortId="vendorName"
                    {...theadProps}
                  />
                  <SortableTHeadCell
                    label={`${vendorLabel} Status`}
                    align="center"
                    sortId="vendorStatus"
                    {...theadProps}
                  />
                  <TableCell align="center">Certificate ID</TableCell>
                  <SortableTHeadCell
                    label="Certificate Status"
                    align="center"
                    sortId="certStatus"
                    {...theadProps}
                  />
                  <TableCell>Policy #</TableCell>
                  <TableCell align="center">Policy Type</TableCell>
                  <SortableTHeadCell
                    label="Policy EXP"
                    sortId="policyExp"
                    {...theadProps}
                  />
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableHead>
                <TableRow>
                  <TableCell/>
                  <TableCell align="center">
                    <ColumnMultiFilter
                      name="vendorStatus"
                      label="Statuses"
                      values={[
                        Vendorstatus.COMPLIANT,
                        Vendorstatus.NOT_COMPLIANT
                      ]}
                      selectedValues={selectedVendorStatuses}
                      setValues={setSelectedVendorStatuses}
                      renderMenuContent={(value) => <StatusIndicator label={value}/>}
                    />
                  </TableCell>
                  <TableCell/>
                  <TableCell align="center">
                    <ColumnMultiFilter
                      name="certStatus"
                      label="Statuses"
                      values={[
                        Certificatestatus.APPROVED,
                        Certificatestatus.REVIEW,
                        Certificatestatus.EXPIRED,
                        Certificatestatus.INACTIVE
                      ]}
                      selectedValues={selectedCertStatuses}
                      setValues={setSelectedCertStatuses}
                      renderMenuContent={(value) => <StatusIndicator label={value}/>}
                    />
                  </TableCell>
                  <TableCell/>
                  <TableCell align="center">
                    <ColumnMultiFilter
                      name="policyType"
                      label="Types"
                      values={[
                        Insurancetype.GL,
                        Insurancetype.AU,
                        Insurancetype.UMB,
                        Insurancetype.WC,
                        Insurancetype.CUSTOM
                      ]}
                      selectedValues={selectedPolicyTypes}
                      setValues={setSelectedPolicyTypes}
                    />
                  </TableCell>
                  <TableCell/>
                  <TableCell/>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && <TableLoadingIndicator />}
                {!isLoading && !filteredCertificates?.length && (
                  <TableRow>
                    <TableCell colSpan={8} height={"50px"} sx={{textAlign: "center"}}>
                      {
                        !isLoading && filteredCertificates?.length === 0 &&
                        `No policies expiring in ${Number(selectedQuarter) ? " Q" + Number(selectedQuarter) + "," : ""} ${selectedYear}`
                      }
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && filteredCertificates?.length > 0 && (
                  filteredCertificates?.map(cert => {
                    let coverages = []
                    try {
                      coverages = JSON.parse(cert.coverages)
                      if(selectedPolicyTypes.length) {
                        coverages = coverages.filter(coverage => selectedPolicyTypes.includes(coverage.insuranceType))
                      }
                    } catch(e) {
                      console.error(e)
                    }

                    return (
                      <Fragment key={cert.id}>
                        <TableRow key={cert.id + "-0"}>
                          <TableCell rowSpan={coverages?.length || 1}>
                            <Link
                              component={"button"}
                              underline="none"
                              className="clickable"
                              color="inherit"
                              onClick={() => navToVendor(clientVendors?.[cert.clientVendorID]?.vendor?.id)}
                            >
                              {clientVendors?.[cert.clientVendorID]?.vendor?.name}
                            </Link>
                          </TableCell>
                          <TableCell rowSpan={coverages?.length || 1} align="center">
                            {clientVendors?.[cert.clientVendorID]?.status && (
                              <StatusIndicator label={clientVendors[cert.clientVendorID].status}/>
                            )}
                          </TableCell>
                          <TableCell rowSpan={coverages?.length || 1} align="center">
                            <Link
                              component={"button"}
                              underline="none"
                              className="clickable"
                              color="inherit"
                              onClick={() => goToCertificate(cert.id)}
                            >
                              {String(cert.id).substring(cert.id.length - 4)}
                            </Link>
                          </TableCell>
                          <TableCell rowSpan={coverages?.length || 1} align="center">
                            <StatusIndicator
                              fullWidth
                              label={cert.status || "No Status"}
                            />
                          </TableCell>
                          <TableCell>{coverages[0]?.policyNumber}</TableCell>
                          <TableCell align="center">{coverages[0]?.insuranceType}</TableCell>
                          <TableCell>
                            <PolicyExpirations policyExp={coverages[0]?.policyExp} quarter={selectedQuarter} />
                          </TableCell>
                          <TableCell rowSpan={coverages?.length || 1} align="center">
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuClick(e, cert.clientVendorID)}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        {
                          coverages?.splice(1).map((policy, policyIdx) => {
                            return (
                              <TableRow key={cert.id + "-" + (policyIdx + 1)}>
                                <TableCell>{policy.policyNumber}</TableCell>
                                <TableCell align="center">{policy.insuranceType}</TableCell>
                                <TableCell>
                                  <PolicyExpirations policyExp={policy.policyExp} quarter={selectedQuarter} />
                                </TableCell>
                              </TableRow>
                            )
                          })
                        }
                      </Fragment>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack direction={"row"} alignItems="middle" gap={1}>
            <EventNote color="action" />
            <Box flexGrow={1}>
              <Typography variant={"subtitle1"} color={"gray"}>
                Showing certificates expiring in{Number(selectedQuarter) ? " Q" + Number(selectedQuarter) + "," : ""} {selectedYear}
              </Typography>
            </Box>
            <Chip
              variant="outlined"
              label={`${filteredCertificates?.length} ${filteredCertificates?.length === 1 ? 'certificate' : 'certificates'}`}
            >
            </Chip>
          </Stack>
        </Stack>
      </CardContent>
      
      <Snackbar message={message} setMessage={setMessage} />
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleSendReminder}>
          Send Reminder
        </MenuItem>
      </Menu>
      
      {selectedVendorForEmail && clientVendors[selectedVendorForEmail] && (
        <EmailConfirmationModal
          open={emailModalOpen}
          onClose={() => {
            setEmailModalOpen(false);
            setSelectedVendorForEmail(null);
          }}
          onEmailSentResult={onEmailSentResult}
          vendorName={clientVendors[selectedVendorForEmail].vendor?.name}
          clientVendorID={selectedVendorForEmail}
          vendorEmail={clientVendors[selectedVendorForEmail].vendor?.email}
          clientName={client?.name}
          clientId={client?.id}
          clientLogo={clientLogo}
          templates={[certificate_actions.REQUEST]}
          certificateId="create"
          getUploadLink={getUploadLink}
          vendorProjects={
            clientVendors[selectedVendorForEmail]?.projects?.items?.map(vp => 
              clientProjects.find(p => p.id === vp.projectID)
            ).filter(Boolean) || []
          }
          defaultProject={clientProjects.find(p => p.isDefault)}
          useClientDefaultProject={client?.useDefaultProject}
          loggedInUser={loggedInUser}
          additionalRecipients={[
            ...(client?.usePrimaryNotificationEmailAddress ? [client?.primaryNotificationEmailAddress] : []), 
            ...(Array.isArray(clientVendors[selectedVendorForEmail].vendor?.additionalEmails) ? clientVendors[selectedVendorForEmail].vendor?.additionalEmails : [])
          ]}
          projectLabel={client?.projectLabel || "Project"}
          clientVendor={clientVendors[selectedVendorForEmail]}
        />
      )}
    </Card>
  );
}

const PolicyExpirations = ({ policyExp = [], quarter = 0 }) => (
  !!policyExp?.length && policyExp.map((exp, idx) => (
    <Fragment key={"policyExp" + idx}>
      <Typography
        component="span"
        color={
        getQuarter(exp) <= getQuarter(new Date()) && getYear(exp) === getYear(new Date())
          ? "error"
          : "default"
      }
      >{awsDateToUS(exp)}</Typography>
      {idx !== policyExp.length - 1 && ", "}
    </Fragment>
  ))
)
